import React from 'react';
import { ClipboardCheck, Scan, Target, TrendingUp } from 'lucide-react';
import { AnimatedSection, SectionBadge } from './shared';

/**
 * MethodSection — La "Méthode Body-Up en 4 étapes".
 * Rassure les visiteurs en montrant le déroulé concret d'un accompagnement.
 */
const MethodSection = ({ language = 'fr' }) => {
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  const steps = [
    {
      n: '01',
      icon: ClipboardCheck,
      title: L('Bilan gratuit', 'Free assessment'),
      desc: L(
        'On échange sur tes objectifs, ton historique sportif, ta disponibilité. Aucune pression, aucun engagement.',
        'We discuss your goals, sports history and availability. No pressure, no commitment.'
      ),
    },
    {
      n: '02',
      icon: Scan,
      title: L('Mesure & analyse', 'Measure & analyze'),
      desc: L(
        'Scan corporel Styku, mesures clés et photos repères pour définir un point de départ précis et visualiser ta progression.',
        'Styku body scan, key measurements and baseline photos for a precise starting point and clear progress tracking.'
      ),
    },
    {
      n: '03',
      icon: Target,
      title: L('Plan sur-mesure', 'Custom plan'),
      desc: L(
        'Programme d\'entraînement adapté à ton niveau et plan nutrition pensé pour ton mode de vie.',
        'Training program tailored to your level + nutrition plan designed for your lifestyle.'
      ),
    },
    {
      n: '04',
      icon: TrendingUp,
      title: L('Suivi & ajustements', 'Follow-up & adjustments'),
      desc: L(
        'Séances 1:1, suivi régulier des progrès et ajustements du plan toutes les 4 semaines pour rester sur la bonne trajectoire.',
        '1:1 sessions, regular progress tracking and plan adjustments every 4 weeks to stay on track.'
      ),
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-zinc-950 to-black relative overflow-hidden" data-testid="method-section">
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <SectionBadge emoji="🧭">{L('LA MÉTHODE BODY-UP', 'THE BODY-UP METHOD')}</SectionBadge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4">
              {L('Comment on travaille', 'How we work')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">
                {L('ensemble en 4 étapes', 'together in 4 steps')}
              </span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
              {L(
                'Une approche structurée et transparente, du premier contact jusqu\'à tes résultats.',
                'A structured, transparent approach — from first contact to your results.'
              )}
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" aria-hidden="true" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <AnimatedSection key={step.n} delay={i * 150}>
                <div className="group relative bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-2xl p-6 hover:border-primary/40 hover:-translate-y-1 transition-all duration-500 h-full">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative shrink-0">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary via-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-black border-2 border-primary text-[10px] font-black text-primary flex items-center justify-center">
                        {step.n}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MethodSection;
